import { Form, Formik } from 'formik';
import { useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import InputField from 'src/components/common/formFields/inputField/InputField';
import EmojiPicker from 'emoji-picker-react';
import Button from 'src/components/button/Button';
import { ActionIcon } from 'src/components/common';
import { EmojiIcon, PlusIcon } from 'src/components/common/common.icons';
import { colors } from 'src/constants';
import { useGetIdentity, useUploadMedia } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  TEditIdeaMessageRequest,
  TGetChatFilePresignedUrlActionRequest,
  TSendIdeaGroupMessageRequest
} from 'src/services/apiEndpoint.types.ts';
import {
  clearChatFiles,
  editIdeaMessage,
  getChatFilePresignedUrl,
  sendIdeaGroupMessage
} from 'src/store/chat/chat.slice';
import FileCard from 'src/components/chat/fileCard/FileCard';
import { openPopup } from 'src/store/common/common.slice';

import './chatMessagesFooter.scss';
import {
  TChatMessagesFooterProps,
  TIdeaGroupMessageInitialValues
} from './chatMessagesFooter.types';

const ChatMessagesFooter = ({
  placeholder,
  groupId,
  editMessageSettings,
  updateMessageSettings,
  groupMessageContext
}: TChatMessagesFooterProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const { getIdentities } = useGetIdentity();
  const { filesToUpload } = useAppSelector((store) => store.chat);
  const { handleUploadMedia } = useUploadMedia();

  // State
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const [messageId, setMessageId] = useState<string>(uuidv4());
  const uploadFileRef = useRef<HTMLInputElement>(null);

  // Constants
  const { authenticatedId, loggedInUserType } = getIdentities();
  const isEditMessageMode: boolean = editMessageSettings?.editMode;

  const initialValues: TIdeaGroupMessageInitialValues = isEditMessageMode
    ? { message: editMessageSettings.message }
    : { message: '' };

  const turnEditModeOff = () => {
    updateMessageSettings({ editMode: false, messageId: '', message: '' });
  };

  const openImageFileManager = async () => {
    if (uploadFileRef.current) {
      uploadFileRef.current.click();
    }
  };

  const onMediaLoad = (
    data: string | ArrayBuffer | null,
    fileType: string,
    mediaType: string,
    name: string,
    size: number
  ) => {
    const fileId: string = uuidv4();

    const payload: TGetChatFilePresignedUrlActionRequest = {
      conversationId: groupId,
      messageId,
      id: fileId,
      fileName: name,
      fileData: data
    };
    dispatch(getChatFilePresignedUrl(payload));
  };

  const openEmptyMessagePopup = () => {
    dispatch(
      openPopup({
        popupMessage: 'You message is empty, Please type something before sending',
        popupType: 'error'
      })
    );
  };

  const handleSubmit = (
    values: TIdeaGroupMessageInitialValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    const { message } = values;

    if (groupId) {
      if (isEditMessageMode) {
        const payload: TEditIdeaMessageRequest = {
          eventName:
            groupMessageContext === 'idea'
              ? 'edit-idea-conversation-message'
              : 'edit-program-conversation-message',
          messageId: editMessageSettings?.messageId,
          message
        };

        if (message?.trim()) {
          dispatch(editIdeaMessage(payload));
          turnEditModeOff();
          resetForm();
        } else {
          openEmptyMessagePopup();
        }
      } else if (!isEditMessageMode) {
        const payload: TSendIdeaGroupMessageRequest = {
          eventName: groupMessageContext === 'idea' ? 'idea-conversation' : 'program-conversation',
          userId: authenticatedId,
          userType: loggedInUserType,
          conversationId: groupId,
          message,
          messageId,
          files: filesToUpload
        };

        if (filesToUpload?.length > 0 || message?.trim()) {
          dispatch(sendIdeaGroupMessage(payload));
          resetForm();
          setMessageId(uuidv4()); // set uuid for the next message
          dispatch(clearChatFiles());
        } else {
          openEmptyMessagePopup();
        }
      }
    }
  };

  return (
    <div className="ih-g-chat-messages-footer">
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ setFieldValue, values }) => {
          return (
            <Form className="ih-g-chat-messages-footer__form">
              <div className="ih-g-chat-messages-footer__form__row">
                <InputField id="message" placeholder={placeholder} value={values?.message}/>
                <div className="ih-g-chat-messages-footer__form__row__actions">
                  {editMessageSettings.editMode && (
                    <Button
                      size="small"
                      variant="secondary"
                      type="button"
                      className="ihg-chat-messages-footer__form__row__actions__cancel"
                      onClick={turnEditModeOff}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button size="small" variant="primary" type="submit">
                    Send
                  </Button>
                </div>
              </div>
              <div className="ih-g-chat-messages-footer__form__row__actions">
                <input
                  type="file"
                  hidden
                  data-testid="inputUploadImage"
                  ref={uploadFileRef}
                  accept=".bmp,.jpg,.jpeg,.png,.pdf,.mp4,.webm"
                  className="select-post-image"
                  multiple
                  onChange={(e) => {
                    handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'program' });
                  }}
                />
                {!editMessageSettings?.editMode && (
                  <ActionIcon onClick={openImageFileManager}>
                    <PlusIcon color={colors?.neutral?.[700]} />
                  </ActionIcon>
                )}
                <ActionIcon onClick={() => setIsEmojiPickerOpen((prev) => !prev)}>
                  <EmojiIcon />
                </ActionIcon>
              </div>{' '}
              <EmojiPicker
                open={isEmojiPickerOpen}
                onEmojiClick={(e) => {
                  setFieldValue('message', values.message + e.emoji);
                  setIsEmojiPickerOpen(false);
                }}
              />
              {filesToUpload?.length > 0 && (
                <div className="ih-g-chat-messages-footer__form__row__files">
                  {filesToUpload.map(({ id, fileName }) => (
                    <FileCard
                      key={id}
                      id={id}
                      fileName={fileName}
                      showDownloadButton={false}
                      showDeleteButton={true}
                    />
                  ))}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChatMessagesFooter;
